import { injectable } from 'tsyringe';
import log from 'loglevel';

export const TOKEN_IMouseState = Symbol('IMouseState');

export interface IMouseState {
    /**
     * Whether the left mouse button is currently being held down
     */
    isLeftButtonDown: boolean;

    isTouchActive: boolean;

    /**
     * Whether the right mouse button is currently being held down
     */
    isRightButtonDown: boolean;

    /**
     * Initializes mouse event listeners
     */
    initialize(): void;

    /**
     * Cleans up mouse event listeners
     */
    cleanup(): void;
}


@injectable()
export class DomMouseState implements IMouseState {
    private _isLeftButtonDown = false;
    private _isRightButtonDown = false;
    private _isTouchActive = false; // Initialize touch state
    private _initialized = false;

    get isLeftButtonDown(): boolean { return this._isLeftButtonDown; }
    get isRightButtonDown(): boolean { return this._isRightButtonDown; }
    get isTouchActive(): boolean { return this._isTouchActive; } // Getter for touch state

    constructor() {
        log.debug('DomMouseState: created');
    }

    public initialize = (): void => {
        if (this._initialized) {
            log.debug('DomMouseState: already initialized, skipping');
            return;
        }

        log.debug('DomMouseState: initializing touch and mouse event listeners');

        // Mouse events
        document.addEventListener('mousedown', this.handleMouseDown);
        document.addEventListener('mouseup', this.handleMouseUp);
        document.addEventListener('mouseleave', this.handleMouseLeave);
        document.addEventListener('contextmenu', this.handleContextMenu);

        // Touch events
        document.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        document.addEventListener('touchend', this.handleTouchEnd);
        document.addEventListener('touchcancel', this.handleTouchCancel);

        this._initialized = true;
    }

    public cleanup = (): void => {
        if (!this._initialized) {
            return;
        }

        log.debug('DomMouseState: cleaning up touch and mouse event listeners');

        // Mouse events
        document.removeEventListener('mousedown', this.handleMouseDown);
        document.removeEventListener('mouseup', this.handleMouseUp);
        document.removeEventListener('mouseleave', this.handleMouseLeave);
        document.removeEventListener('contextmenu', this.handleContextMenu);

        // Touch events
        document.removeEventListener('touchstart', this.handleTouchStart);
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleTouchEnd);
        document.removeEventListener('touchcancel', this.handleTouchCancel);

        this._initialized = false;
        this._isLeftButtonDown = false;
        this._isRightButtonDown = false;
        this._isTouchActive = false; // Reset touch state
    }

    private handleMouseDown = (event: MouseEvent): void => {
        log.debug('DomMouseState: mouse down', event.button);

        if (event.button === 0) {
            this._isLeftButtonDown = true;
        } else if (event.button === 2) {
            this._isRightButtonDown = true;
        }
    }

    private handleMouseUp = (event: MouseEvent): void => {
        log.debug('DomMouseState: mouse up', event.button);
        if (event.button === 0) {
            this._isLeftButtonDown = false;
        } else if (event.button === 2) {
            this._isRightButtonDown = false;
        }
    }

    private handleMouseLeave = (): void => {
        log.debug('DomMouseState: mouse leave, resetting button states');
        this._isLeftButtonDown = false;
        this._isRightButtonDown = false;
        this._isTouchActive = false;
    }

    private handleContextMenu = (event: MouseEvent): void => {
        log.debug('DomMouseState: context menu event');
        event.preventDefault();
    }

    // New touch event handlers
    private handleTouchStart = (): void => {
        log.debug('DomMouseState: touch start');
        this._isTouchActive = true;
    }

    private handleTouchMove = (event: TouchEvent): void => {
        if (this._isTouchActive) {
            log.debug('DomMouseState: touch move');
            event.preventDefault();
        }
    }

    private handleTouchEnd = (): void => {
        log.debug('DomMouseState: touch end');
        this._isTouchActive = false;
    }

    private handleTouchCancel = (): void => {
        log.debug('DomMouseState: touch cancel');
        this._isTouchActive = false;
    }
}